import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

interface NavItem {
  icon: string;
  label: string;
  route: string;
  matchRoutes?: string[]; // Additional routes that should highlight this item
  comingSoon?: boolean;
}

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  standalone: false
})
export class SideNavComponent implements OnInit, OnDestroy {
  navItems: NavItem[] = [
    { icon: 'search', label: 'Patient Search', route: '/search?tab=patients', matchRoutes: ['/patient-details'] },
    { icon: 'people', label: 'Teammate Search', route: '/search?tab=teammates' },
    { icon: 'medical_services', label: 'Provider Search', route: '/search?tab=providers' },
    { icon: 'business', label: 'Facility Search', route: '/search?tab=facility', comingSoon: true },
    { icon: 'volunteer_activism', label: 'Care Team Lookup', route: '/search?tab=careteamlookup', matchRoutes: ['/care-team-lookup'] },
    { icon: 'event', label: 'Appointment Lookup', route: '/search?tab=appointments' },
    { icon: 'history', label: 'Recent', route: '/recent' }
  ];

  currentRoute: string = '';
  private routerSub: Subscription | null = null;

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.currentRoute = this.router.url;
    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.currentRoute = event.urlAfterRedirects || event.url;
    });
  }

  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  isActive(item: NavItem): boolean {
    // Extract base path from item route (remove query params)
    const itemBasePath = item.route.split('?')[0];
    const currentBasePath = this.currentRoute.split('?')[0];

    // Check if we're on a matching route
    if (currentBasePath.startsWith(itemBasePath)) {
      // For /search routes, check if we're on the right tab
      if (itemBasePath === '/search') {
        const currentParams = new URLSearchParams(this.currentRoute.split('?')[1] || '');
        const itemParams = new URLSearchParams(item.route.split('?')[1] || '');
        const currentTab = currentParams.get('tab') || 'patients';
        const itemTab = itemParams.get('tab') || 'patients';
        return currentTab === itemTab;
      }
      return true;
    }
    
    // Check additional match routes
    if (item.matchRoutes) {
      return item.matchRoutes.some(route => currentBasePath.startsWith(route));
    }
    
    return false;
  }

  navigateTo(route: string): void {
    // Handle routes with query params
    if (route.includes('?')) {
      const [path, queryString] = route.split('?');
      const params = new URLSearchParams(queryString);
      const queryParams: any = {};
      params.forEach((value, key) => queryParams[key] = value);
      // Force navigation even if already on same route by adding timestamp
      // This ensures queryParams subscription fires
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([path], { queryParams });
      });
    } else {
      this.router.navigate([route]);
    }
  }
}
